// src/app/core/shared/me.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';

export type AllowedTier = 't0' | 't3' | 't6';

export interface MeCity {
  city_id: number;
  city_name: string;
  city_specification?: string;
  city_t0_status?: string;
  city_t3_status?: string;
  city_t6_status?: string;
  country_name?: string;
}

export interface MeUserMapping {
  ALL?: boolean;
  [feature: string]: any;
}

export interface MeBackendUser {
  user_email?: string;
  email?: string;
  all_access?: boolean;
  user_admin_access?: string; // "yes" | "no"
  user_mapping?: MeUserMapping;
}

export interface MeBackendResponse {
  status: 'success' | 'fail';
  allowed?: boolean;
  cities?: MeCity[];
  session?: {
    session_id?: string;
    session_ip?: string;
  };
  user?: MeBackendUser;
  message?: string;
}

const LS_ME_KEY = 'me_cache_v1';
const LOCAL_ME_JSON_PATH = 'assets/json-data/user_verification.json';

@Injectable({ providedIn: 'root' })
export class MeService {
  private meCache: MeBackendResponse | null = null;
  private readonly meUrl: string;

  constructor(private http: HttpClient) {
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    this.meUrl = isLocalhost ? '/user_verification' : `${environment.apiUrl}/user_verification`;
    this.hydrateFromLocalStorage();
  }

  async checkAllowlist(options: { email: string; sessionId: string; forceRefresh?: boolean }): Promise<MeBackendResponse> {
    const force = !!options.forceRefresh;

    if (!force) {
      const cached = this.meCache ?? this.readFromLocalStorage();
      if (cached) {
        this.meCache = cached;
        this.applyToSessionStorage(cached);
        return cached;
      }
    }

    const body = {
      email: (options.email || '').trim().toLowerCase(),
      session_id: (options.sessionId || '').trim()
    };

    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (isLocalhost && force) {
      this.clearCache();
    }

    const res = isLocalhost
      ? await firstValueFrom(this.http.get<MeBackendResponse>(LOCAL_ME_JSON_PATH))
      : await firstValueFrom(this.http.post<MeBackendResponse>(this.meUrl, body));

    this.meCache = res;
    this.writeToLocalStorage(res);
    this.applyToSessionStorage(res);

    try {
      window.dispatchEvent(new CustomEvent('me-data-ready'));
    } catch { }

    return res;
  }

  clearCache(): void {
    this.meCache = null;

    try {
      localStorage.removeItem(LS_ME_KEY);
    } catch { }

    try {
      sessionStorage.removeItem('meStatus');
      sessionStorage.removeItem('meAllowed');
      sessionStorage.removeItem('userEmailFromMe');
      sessionStorage.removeItem('userLevelFromMe');
      sessionStorage.removeItem('cityNameFromMe');
      sessionStorage.removeItem('allowedTiersFromMe');
      sessionStorage.removeItem('allowedCityNamesFromMe');
      sessionStorage.removeItem('userMappingFromMe');
      sessionStorage.removeItem('allAccessFromMe');
      sessionStorage.removeItem('userAdminAccessFromMe');
    } catch { }
  }

  private hydrateFromLocalStorage(): void {
    const cached = this.readFromLocalStorage();
    if (!cached) {
      return;
    }
    this.meCache = cached;
    this.applyToSessionStorage(cached);
  }

  private readFromLocalStorage(): MeBackendResponse | null {
    try {
      const raw = localStorage.getItem(LS_ME_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as MeBackendResponse;
      if (!parsed || typeof parsed !== 'object' || !parsed.status) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private writeToLocalStorage(res: MeBackendResponse): void {
    try {
      localStorage.setItem(LS_ME_KEY, JSON.stringify(res));
    } catch { }
  }

  private applyToSessionStorage(res: MeBackendResponse): void {
    try {
      sessionStorage.removeItem('meStatus');
      sessionStorage.removeItem('meAllowed');
      sessionStorage.removeItem('userEmailFromMe');
      sessionStorage.removeItem('userLevelFromMe');
      sessionStorage.removeItem('cityNameFromMe');
      sessionStorage.removeItem('allowedTiersFromMe');
      sessionStorage.removeItem('allowedCityNamesFromMe');
      sessionStorage.removeItem('userMappingFromMe');
      sessionStorage.removeItem('allAccessFromMe');
      sessionStorage.removeItem('userAdminAccessFromMe');

      sessionStorage.setItem('meStatus', res.status || '');

      if (typeof res.allowed === 'boolean') {
        sessionStorage.setItem('meAllowed', String(res.allowed));
      }

      const returnedEmail = res?.user?.user_email || res?.user?.email || '';
      if (returnedEmail) {
        sessionStorage.setItem('userEmailFromMe', returnedEmail);
      }

      const allAccess = !!res?.user?.all_access;
      sessionStorage.setItem('allAccessFromMe', String(allAccess));

      const isAdmin = String(res?.user?.user_admin_access || '').trim().toLowerCase() === 'yes';
      sessionStorage.setItem('userAdminAccessFromMe', isAdmin ? 'yes' : 'no');

      const userMapping = res?.user?.user_mapping || {};
      sessionStorage.setItem('userMappingFromMe', JSON.stringify(userMapping));

      const allowedTiers = this.extractAllowedTiers(res);
      sessionStorage.setItem('allowedTiersFromMe', JSON.stringify(allowedTiers));

      const allowedCityNamesLower = this.extractAllowedCityNamesLower(res);
      sessionStorage.setItem('allowedCityNamesFromMe', JSON.stringify(allowedCityNamesLower));

      const defaultCity = this.extractDefaultCityName(res);
      if (defaultCity) {
        sessionStorage.setItem('cityNameFromMe', defaultCity);
      } else {
        sessionStorage.removeItem('cityNameFromMe');
      }

      const syntheticLevel = this.allowedTiersToLegacyLevel(allowedTiers);
      sessionStorage.setItem('userLevelFromMe', syntheticLevel);
    } catch { }
  }

  private extractAllowedTiers(res: MeBackendResponse): AllowedTier[] {
    if (res?.user?.all_access) {
      return ['t0', 't3', 't6'];
    }

    const dashboard = res?.user?.user_mapping?.['dashboard'] || {};
    const out: AllowedTier[] = [];

    if (Array.isArray(dashboard?.T0) && dashboard.T0.length > 0) {
      out.push('t0');
    }

    if (Array.isArray(dashboard?.T3) && dashboard.T3.length > 0) {
      out.push('t3');
    }

    if (Array.isArray(dashboard?.T6) && dashboard.T6.length > 0) {
      out.push('t6');
    }

    return out;
  }

  private extractAllowedCityNamesLower(res: MeBackendResponse): string[] {
    const allAccess = !!res?.user?.all_access;

    if (allAccess) {
      const fromCities = Array.isArray(res?.cities)
        ? res.cities.map((c) => String(c?.city_name || '').trim()).filter(Boolean)
        : [];

      return Array.from(new Set(fromCities.map((x) => x.toLowerCase())));
    }

    const mapping = res?.user?.user_mapping || {};
    const collected: string[] = [];

    Object.keys(mapping).forEach((featureKey) => {
      const feature = mapping[featureKey];
      if (!feature || typeof feature !== 'object' || Array.isArray(feature)) {
        return;
      }

      Object.keys(feature).forEach((tierKey) => {
        const cities = feature[tierKey];
        if (Array.isArray(cities)) {
          collected.push(...cities.map((x: any) => String(x || '').trim()).filter(Boolean));
        }
      });
    });

    const fromResponseCities = Array.isArray(res?.cities)
      ? res.cities.map((c) => String(c?.city_name || '').trim()).filter(Boolean)
      : [];

    return Array.from(new Set([...collected, ...fromResponseCities].map((x) => x.toLowerCase())));
  }

  private extractDefaultCityName(res: MeBackendResponse): string {
    const allAccess = !!res?.user?.all_access;

    if (allAccess) {
      return String(res?.cities?.[0]?.city_name || '').trim();
    }

    const dashboard = res?.user?.user_mapping?.['dashboard'] || {};
    const ordered = [
      ...(Array.isArray(dashboard?.T0) ? dashboard.T0 : []),
      ...(Array.isArray(dashboard?.T3) ? dashboard.T3 : []),
      ...(Array.isArray(dashboard?.T6) ? dashboard.T6 : [])
    ]
      .map((x: any) => String(x || '').trim())
      .filter(Boolean);

    return ordered[0] || '';
  }

  private allowedTiersToLegacyLevel(tiers: AllowedTier[]): string {
    const hasT0 = tiers.includes('t0');
    const hasT3 = tiers.includes('t3');
    const hasT6 = tiers.includes('t6');

    if (hasT0 && hasT3 && hasT6) {
      return 'ALL';
    }
    if (hasT6) {
      return 'T6';
    }
    if (hasT3) {
      return 'T3';
    }
    if (hasT0) {
      return 'T0';
    }
    return 'T0';
  }
}